import React, { FC, useCallback, useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { map, orderBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { IconTypes } from 'components/Icons';
import {
  renderSideNavHeader,
  SideNav,
  sideNavCategoryHeaderStyles,
  SideNavMenuItemProps,
} from 'components/Navigation/SideNav';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { AllFindings } from 'containers/Findings/FindingsOverview/Components/AlertTables/AllFindings';
import { useFindingsContext } from 'containers/Findings/FindingsOverview/Components/hooks/FindingsContext';
import { IndividualFindingCategoryDashboard } from 'containers/Findings/FindingsOverview/Components/IndividualCategoryDashboard';
import { FindingsOverviewDashboard } from 'containers/Findings/FindingsOverview/Components/Overview';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { actions } from 'containers/Findings/FindingsOverview/slice';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

import { hasSideNavItem } from './utils';

export const FindingsOverviewSideNav: FC = () => {
  const dispatch = useDispatch();
  const params = useSearchParams<{
    categoryClass: string;
    alert_category: string;
    alertCategories: string[];
    tab: string;
    redirect: string;
  }>();

  const { currentCategory } = useSelector(selectFindingsOverview);

  const [items, setItems] = useState<SideNavMenuItemProps[]>([]);

  const { toggleRefresh } = useFindingsContext();
  const { aiParamExtraction, alertCategories } = useSelector(selectSharedState);
  const { updateValueInURL } = useSearchParamFunctions();

  const redirectTo = useCallback(
    (tab: string) => {
      updateValueInURL('tab', tab);
    },
    [updateValueInURL],
  );

  const onOpenLearnMore = useCallback(() => {
    dispatch(sharedStateActions.updateLearnMore('findings'));
  }, [dispatch]);

  // create sidebar items
  useEffect(() => {
    const categories = map(alertCategories.data?.categories, (each, key) => {
      return {
        // shows up in sidebar
        title: <Box pl={1}>{toTitleCase(key)}</Box>,
        key: key,
        component: (
          <IndividualFindingCategoryDashboard
            title={toTitleCase(key)}
            onOpenLearnMore={onOpenLearnMore}
          />
        ),
      };
    });
    const orderedCategories = orderBy(categories, ['key']);

    const items: SideNavMenuItemProps[] = [
      {
        title: renderSideNavHeader('Overview', IconTypes.DashboardOverview),
        key: 'Overview',
        component: (
          <FindingsOverviewDashboard onOpenLearnMore={onOpenLearnMore} />
        ),
      },
      {
        title: renderSideNavHeader('All', IconTypes.DashboardAll),
        key: 'AllFindings',
        component: <AllFindings onOpenLearnMore={onOpenLearnMore} />,
        showDivider: true,
      },
      {
        title: renderSideNavHeader(
          'By Categories',
          IconTypes.DashboardCategory,
        ),
        key: 'findingsCategories',
        showArrow: false,
        styles: sideNavCategoryHeaderStyles,
        secondary: orderedCategories,
      },
    ];
    setItems(items);
  }, [toggleRefresh, alertCategories.data, onOpenLearnMore, currentCategory]);

  //update tab based on search
  useEffect(() => {
    !!aiParamExtraction.data?.category_class &&
      dispatch(
        actions.updateCurrentCategory(aiParamExtraction.data?.category_class),
      );
  }, [aiParamExtraction.data?.category_class, dispatch]);

  //update tab based on url
  useEffect(() => {
    let tab = '';
    if (!!params?.tab) tab = params.tab;
    else if (!!params?.categoryClass) tab = params?.categoryClass;
    if (!tab || !items.length) return;

    // Wait until categories are loaded before validating category tabs. The
    // initial menu only contains Overview and AllFindings.
    const categoriesLoaded =
      alertCategories.isSuccess || alertCategories.isError;
    if (!categoriesLoaded && tab !== 'Overview' && tab !== 'AllFindings') {
      return;
    }

    dispatch(
      actions.updateCurrentCategory(
        hasSideNavItem(items, tab) ? tab : 'Overview',
      ),
    );
  }, [
    alertCategories.isError,
    alertCategories.isSuccess,
    dispatch,
    items,
    params,
  ]);

  return (
    <Box h="full">
      <SideNav
        leftAlignSecondaryItems
        menuItems={items}
        current={currentCategory}
        onChange={current => {
          redirectTo(current as string);
        }}
        height="full"
        styles={{
          contentOuterWrapper: { card: { p: 0, bg: 'transparent' } },
          menu: { index: [2] },
        }}
        showContentHeader={false}
        isLoading={alertCategories.isLoading}
      />
      <LearnMore />
    </Box>
  );
};
